import { IClassListItem } from "../../../model/respone/classes/IClassListItem";
import { hasScheduleConflict } from "../../../helpers/scheduleConflict";
import { IClassesReducer } from "../../reducer-model/classes/IClassesReducer";
import { eReducerStatusBase } from "../../reducer-model/eReducerStatusBase";

const initialState: IClassesReducer = {
    status: eReducerStatusBase.is_not_initialization,
    list: [],
    error_message: "",
};

const parseTrungLichCsv = (csv?: string): number[] => {
    if (!csv) return [];
    return csv
        .split(",")
        .map(s => parseInt(s.trim(), 10))
        .filter(n => !isNaN(n) && n > 0);
};

const toCsv = (ids: number[]): string | undefined =>
    ids.length === 0 ? undefined : Array.from(new Set(ids)).join(",");

/**
 * Listen cross-domain registration actions để optimistic-update slotsLeft + trùng lịch
 * → tránh full GET /api/registration/classes mỗi khi register/cancel (50k SV peak = 350k extra calls).
 * Action type loose-typed (any) vì cross-domain — accept cả CLASSES_* và REG_*.
 */
export const classesReducer = (state: IClassesReducer = initialState, action: any): IClassesReducer => {
    switch (action.type) {
        case "CLASSES_LOAD_START":
            return { ...state, status: eReducerStatusBase.is_loading, error_message: "" };
        case "CLASSES_LOAD_SUCCESS":
            return { ...state, status: eReducerStatusBase.is_completed, list: action.payload };
        case "CLASSES_LOAD_ERROR":
            return { ...state, status: eReducerStatusBase.is_error, error_message: action.payload };

        // Bulk update slot từ /api/registration/slots (user chọn môn / click Tải lại).
        // Payload: Record<classId, slotsLeft>. Lớp không có trong payload → giữ nguyên slotsLeft cũ.
        case "CLASSES_UPDATE_SLOTS": {
            const slots: Record<number, number> = action.payload || {};
            return {
                ...state,
                list: state.list.map(c =>
                    slots[c.classId] !== undefined ? { ...c, slotsLeft: slots[c.classId] } : c
                ),
            };
        }

        // Optimistic local cập nhật khi SignalR confirm register cho lớp này:
        //  • lớp vừa register: -1 slot, flag canRegister=false (ALREADY_REGISTERED)
        //  • lớp khác trùng lịch: thêm classId vào iD_lops_TrungLich + flag SCHEDULE_CONFLICT
        // Tránh lỗi UX "đăng ký xong còn thấy lớp trùng vẫn enable, click mới báo trùng".
        case "REG_REGISTER_CONFIRMED": {
            const newRegId: number = action.payload.classId;
            const registeredClass = state.list.find(c => c.classId === newRegId);
            return {
                ...state,
                list: state.list.map((c: IClassListItem) => {
                    if (c.classId === newRegId) {
                        return {
                            ...c,
                            slotsLeft: Math.max(0, c.slotsLeft - 1),
                            canRegister: false,
                            reasonCode: "ALREADY_REGISTERED",
                            message: "Đã đăng ký lớp này.",
                        };
                    }
                    if (registeredClass &&
                        hasScheduleConflict(c.thoiKhoaBieus, registeredClass.thoiKhoaBieus)) {
                        const trungIds = parseTrungLichCsv(c.iD_lops_TrungLich);
                        if (!trungIds.includes(newRegId)) trungIds.push(newRegId);
                        return {
                            ...c,
                            iD_lops_TrungLich: toCsv(trungIds),
                            canRegister: false,
                            // Giữ reasonCode/message gốc nếu đã có lý do khác (vd OUT_OF_PHAM_VI),
                            // chỉ set SCHEDULE_CONFLICT khi trước đó là eligible.
                            reasonCode: c.reasonCode ?? "SCHEDULE_CONFLICT",
                            message: c.message ?? "Trùng lịch với lớp đã đăng ký.",
                        };
                    }
                    return c;
                }),
            };
        }

        // Optimistic local khi cancel — slot trả lại Redis qua RollbackAsync.
        //  • lớp vừa cancel: +1 slot, restore canRegister nếu trước đó chỉ fail vì ALREADY_REGISTERED
        //  • lớp khác: remove cancelledId khỏi iD_lops_TrungLich; nếu hết conflict + reason là
        //    SCHEDULE_CONFLICT (do reducer set trước đó) → restore canRegister.
        case "REG_CANCEL_SUCCESS": {
            const cancelledId: number = action.payload.classId;
            return {
                ...state,
                list: state.list.map((c: IClassListItem) => {
                    if (c.classId === cancelledId) {
                        const wasOnlyAlreadyRegistered = c.reasonCode === "ALREADY_REGISTERED";
                        return {
                            ...c,
                            slotsLeft: c.slotsLeft + 1,
                            canRegister: wasOnlyAlreadyRegistered ? true : c.canRegister,
                            reasonCode: wasOnlyAlreadyRegistered ? undefined : c.reasonCode,
                            message: wasOnlyAlreadyRegistered ? undefined : c.message,
                        };
                    }
                    const trungIds = parseTrungLichCsv(c.iD_lops_TrungLich);
                    if (!trungIds.includes(cancelledId)) return c;
                    const newTrung = trungIds.filter(id => id !== cancelledId);
                    const stillConflicted = newTrung.length > 0;
                    const restorable = !stillConflicted && c.reasonCode === "SCHEDULE_CONFLICT";
                    return {
                        ...c,
                        iD_lops_TrungLich: toCsv(newTrung),
                        canRegister: restorable ? true : c.canRegister,
                        reasonCode: restorable ? undefined : c.reasonCode,
                        message: restorable ? undefined : c.message,
                    };
                }),
            };
        }

        default:
            return state;
    }
};
